import type { BedrockCatalogModel, OrgLlmModel } from "@/shared/domain/orgs";
import {
  isRobotoManaged,
  offerBlocker,
} from "@/shared/domain/orgs/orgModelConfig";

/** A Roboto-managed model the editor offers to add to the list. */
export interface RobotoModelChoice {
  /** The entry as it goes on the list: not the default, in the chat picker. */
  model: OrgLlmModel;
  /** A catalog model new orgs are not offered (Roboto admins only). */
  notOfferedToNewOrgs?: boolean;
  /** Why it cannot be put on an org yet (e.g. "Verify first."); shown but not selectable. */
  unavailableReason?: string;
}

/** How a choice reads in the add picker. */
export function robotoChoiceLabel(choice: RobotoModelChoice): string {
  if (choice.unavailableReason !== undefined) {
    return `${choice.model.label} (${choice.unavailableReason.replace(/\.$/, "").toLowerCase()})`;
  }
  return choice.notOfferedToNewOrgs === true
    ? `${choice.model.label} (not offered to new orgs)`
    : choice.model.label;
}

function listEntry(
  model: Pick<
    OrgLlmModel,
    "id" | "label" | "description" | "model_id" | "context_window_tokens"
  >,
): OrgLlmModel {
  return {
    id: model.id,
    label: model.label,
    description: model.description,
    model_id: model.model_id,
    context_window_tokens: model.context_window_tokens,
    connection_id: null,
    is_default: false,
    is_utility: false,
    in_picker: true,
  };
}

/**
 * The Roboto-managed models the editor may add:
 *
 * - For Roboto admins (`catalog` given, chat entries only): the catalog models
 *   offered to new orgs or already on the org's saved list. The latter are
 *   marked when new orgs are not offered them; a model not ready for orgs
 *   carries the reason.
 * - For everyone else: the template's Roboto models.
 *
 * Either way, plus any Roboto model on the saved list that neither names, so
 * one taken off in the editor can go back.
 */
export function robotoModelChoices({
  catalog,
  templateModels,
  savedModels,
}: {
  catalog: readonly BedrockCatalogModel[] | undefined;
  templateModels: readonly OrgLlmModel[] | undefined;
  savedModels: readonly OrgLlmModel[];
}): RobotoModelChoice[] {
  const onOrg = new Set(savedModels.filter(isRobotoManaged).map((m) => m.id));
  const choices: RobotoModelChoice[] =
    catalog
      ?.filter((m) => !m.catalog_only || onOrg.has(m.id))
      .map((m) => ({
        model: listEntry(m),
        notOfferedToNewOrgs: m.catalog_only,
        // A model already on the org may go back on even if it could not be
        // added afresh; the server applies the same rule.
        unavailableReason: onOrg.has(m.id) ? undefined : offerBlocker(m),
      })) ??
    templateModels?.map((m) => ({ model: listEntry(m) })) ??
    [];
  for (const saved of savedModels) {
    if (
      isRobotoManaged(saved) &&
      !choices.some((c) => c.model.id === saved.id)
    ) {
      choices.push({ model: listEntry(saved) });
    }
  }
  return choices;
}
